import { createPortal } from 'react-dom';
import { useEffect, useRef } from 'react';

export function Overlay({
  title,
  onClose,
  children,
  closeLabel = 'Close',
}: {
  title: string;
  closeLabel?: string;
  onClose(): void;
  children: React.ReactNode;
}): JSX.Element {
  const panel = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Where focus came from, so closing does not drop the keyboard user at the
    // top of the page with no idea where they are.
    const opener = document.activeElement as HTMLElement | null;
    panel.current?.focus();

    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        onClose();
        return;
      }
      if (event.key !== 'Tab' || !panel.current) return;
      // `aria-modal` is a promise to assistive technology that the rest of the
      // page is inert. Tab has to honour it or the promise is a lie.
      const focusable = panel.current.querySelectorAll<HTMLElement>(
        // `select` is in the list because the share dialog has two of them. A
        // control the trap does not know about is a control Tab can escape the
        // dialog through, which is exactly the promise `aria-modal` makes.
        'a[href], button:not(:disabled), input:not(:disabled), select:not(:disabled), textarea:not(:disabled), [tabindex]:not([tabindex="-1"])',
      );
      if (focusable.length === 0) return;
      const first = focusable[0]!;
      const last = focusable[focusable.length - 1]!;
      if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      } else if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
      opener?.focus?.();
    };
  }, [onClose]);

  return createPortal(
    <div className="overlay" role="dialog" aria-modal="true" aria-label={title}>
      <button className="overlay-scrim" aria-label="Close" onClick={onClose} />
      <div className="overlay-panel" ref={panel} tabIndex={-1}>
        <header className="overlay-head">
          <h2>{title}</h2>
          <button className="icon-button" onClick={onClose} aria-label={closeLabel}>
            <span aria-hidden="true">✕</span>
          </button>
        </header>
        <div className="overlay-body">{children}</div>
      </div>
    </div>,
    document.body,
  );
}

